import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { Dayjs } from "dayjs";
import Grid, { GridBaseProps } from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import DateRangePicker, {
  DateRangePreset,
} from "../../common/DateRangePicker";
import { Branch } from "../../../types/user.type";
import FilterField from "../shared/FilterField";
import { ALL_FILTER_VALUE } from "../shared/constants";
import {
  PRICE_BOOK_STATUS_FILTER_OPTIONS,
  PRICE_BOOK_TYPE_FILTER_OPTIONS,
} from "./constants";

// The free-text search and the date range take the wide slots of the desktop row; the
// selects only ever hold a short value (3 + 2 + 2 + 2 + 3 = 12).
const SEARCH_SIZE: GridBaseProps["size"] = { xs: 12, lg: 3 };
const TYPE_SIZE: GridBaseProps["size"] = { xs: 12, sm: 6, lg: 2 };
const STATUS_SIZE: GridBaseProps["size"] = { xs: 12, sm: 6, lg: 2 };
const BRANCH_SIZE: GridBaseProps["size"] = { xs: 12, sm: 6, lg: 2 };
const DATE_RANGE_SIZE: GridBaseProps["size"] = { xs: 12, sm: 6, lg: 3 };

interface PriceBookFilterProps {
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  priceBookType: string;
  setPriceBookType: Dispatch<SetStateAction<string>>;
  status: string;
  setStatus: Dispatch<SetStateAction<string>>;
  /** Company id of the branch whose shops the listed books must be open to, or the "all" sentinel. */
  branchCompanyId: string;
  setBranchCompanyId: Dispatch<SetStateAction<string>>;
  branches: Branch[];
  createdFrom: Dayjs;
  createdTo: Dayjs;
  onChangeCreatedRange: (start: Dayjs, end: Dayjs) => void;
  /** Oldest selectable day — the same bound the "ทั้งหมดที่มี" preset opens on. */
  createdMinDate: Dayjs;
  presetGroups: DateRangePreset[][];
}

export default function PriceBookFilter({
  search,
  setSearch,
  priceBookType,
  setPriceBookType,
  status,
  setStatus,
  branchCompanyId,
  setBranchCompanyId,
  branches,
  createdFrom,
  createdTo,
  onChangeCreatedRange,
  createdMinDate,
  presetGroups,
}: PriceBookFilterProps) {
  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleChangeType = (event: ChangeEvent<HTMLInputElement>) => {
    setPriceBookType(event.target.value);
  };

  const handleChangeStatus = (event: ChangeEvent<HTMLInputElement>) => {
    setStatus(event.target.value);
  };

  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    setBranchCompanyId(event.target.value);
  };

  return (
    <Grid container spacing={2} mb={2}>
      <FilterField size={SEARCH_SIZE} label="ค้นหา">
        <TextField
          size="small"
          fullWidth
          placeholder="รหัส / ชื่อสมุดราคา"
          value={search}
          onChange={handleChangeSearch}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
      </FilterField>
      <FilterField size={TYPE_SIZE} label="ประเภท">
        <TextField
          select
          size="small"
          fullWidth
          value={priceBookType}
          onChange={handleChangeType}
        >
          {PRICE_BOOK_TYPE_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField size={STATUS_SIZE} label="สถานะ">
        <TextField
          select
          size="small"
          fullWidth
          value={status}
          onChange={handleChangeStatus}
        >
          {PRICE_BOOK_STATUS_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField size={BRANCH_SIZE} label="ใช้ในสาขา">
        <TextField
          select
          size="small"
          fullWidth
          value={branchCompanyId}
          onChange={handleChangeBranch}
        >
          <MenuItem value={ALL_FILTER_VALUE}>ทั้งหมด</MenuItem>
          {branches.map((branch) => (
            <MenuItem key={branch.company_id} value={branch.company_id}>
              {branch.branch_code} {branch.branch_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField size={DATE_RANGE_SIZE} label="วันที่สร้าง">
        <DateRangePicker
          dateFrom={createdFrom}
          dateTo={createdTo}
          onChange={onChangeCreatedRange}
          minDate={createdMinDate}
          presetGroups={presetGroups}
        />
      </FilterField>
    </Grid>
  );
}
